import { HttpContext } from '@angular/common/http';
import { inject } from '@angular/core';
import { HTTP_CONFIG } from '@partners/http';
import { IdentityStore, ShellStore } from '@partners/state';
import { generateGuid } from '@partners/utils';
import { ITrackerConfig, ITrackerParams, TRACKER_CONFIG, TRACKER_HTTP_CONTEXT, TRACKER_PARAMS, TrackerService } from '@partners/worker';

/**
 * Configuration Provider
 * ----------------------
 * This provider injects unified configuration and route-parameter data into
 * every routed feature view. It attaches to Angular’s route-level dependency
 * injection system so that each activated route automatically receives the
 * latest config object merged with its own runtime parameters.
 *
 * Usage:
 * - A custom tracker directive is applied via "hostDirective" to the BaseFeatureView.
 * - The directive runs after the view has been initialized (AfterViewInit),
 *   enabling tracking of any route transition once the view is fully rendered.
 *
 * Purpose:
 * - Supply all routed features with consistent config + params data.
 * - Allow the base directive to track any route after view initialization.
 * - Centralize extraction of route params and configuration logic.
 */

export const provideTrackerConfig = ()=>  [
  TrackerService,
  {
    provide: TRACKER_CONFIG,
    useFactory: (): ITrackerConfig => {
        try {
          const config = inject(HTTP_CONFIG);

          const httpContext = new HttpContext();
          // set any context values if needed
          httpContext.set(TRACKER_HTTP_CONTEXT, { ignoreServerErrors: true });

          const trackerConfig = { url: config.trackerUrl,httpContext: httpContext  } as ITrackerConfig;

          return trackerConfig;
        } catch (error) {return {} as ITrackerConfig;}
    }
  }
];

export const provideTrackerParams = (trackerParams: Partial<ITrackerParams>)=>  [
  {
    provide: TRACKER_PARAMS,
    useFactory: (): ITrackerParams => {
      try {
        const shellStore = inject(ShellStore);
        const identityStore = inject(IdentityStore);

        // get the screen name  from store or from url
        const currentUri = new URL(window.location.href); 
        const screenName = trackerParams?.screenName || currentUri?.pathname;

        // ensure clientProcessId exists in store, if not generate one
        const clientProcessId = shellStore.clientProcessId$$();
        if (!clientProcessId) {
          shellStore.dispatchSaveClientProcessId(generateGuid() as string);
        }

        const params = { 
          screenName, 
          clientProcessId: clientProcessId || shellStore.clientProcessId$$(), 
          opportunityID: identityStore.opportunityId$$(),
          processCode: shellStore.processCode$$(),
          screenCode: shellStore.screenCode$$(screenName)() // get current screen code from store.stages based on screen name (the currentScreen node does not updated in this step, so we need to get the screen code based on screen name)
        } as ITrackerParams;

        return params;
      } catch (error) {
        return {} as ITrackerParams;
      }
    }
  }
];